// Global state to track if there are unsaved changes
let hasUnsavedChanges = false;

// Track all forms being monitored
const monitoredForms = new Set();

// Store original form data for comparison
const formOriginalData = new WeakMap();
let suppressNextPopstate = false;
let leaveInProgress = false;

/**
 * Get serialized form data as a string for comparison
 * @param {HTMLFormElement} form - The form element
 * @returns {string} Serialized form data
 */
function getFormData(form) {
  const formData = new FormData(form);
  const data = {};
  
  // Convert FormData to object
  for (let [key, value] of formData.entries()) {
    if (data[key]) {
      // Handle multiple values (like checkboxes)
      if (Array.isArray(data[key])) {
        data[key].push(value);
      } else {
        data[key] = [data[key], value];
      }
    } else {
      data[key] = value;
    }
  }
  
  // Also capture checkbox states (unchecked boxes don't appear in FormData)
  const checkboxes = form.querySelectorAll('input[type="checkbox"]');
  checkboxes.forEach(checkbox => {
    if (!checkbox.name) return;
    if (!data[checkbox.name]) {
      data[checkbox.name] = checkbox.checked ? 'on' : 'off';
    }
  });
  
  // Capture CKEditor content if CKEditor is available
  if (typeof CKEDITOR !== 'undefined') {
    const textareas = form.querySelectorAll('textarea');
    textareas.forEach(textarea => {
      if (textarea.id && CKEDITOR.instances[textarea.id]) {
        const editor = CKEDITOR.instances[textarea.id];
        // Override textarea value with CKEditor content
        data[textarea.name] = editor.getData();
      }
    });
  }
  
  // Capture Flatpickr values (already in FormData but ensure consistency)
  const dateTimeInputs = form.querySelectorAll('input[type="text"]');
  dateTimeInputs.forEach(input => {
    if (input._flatpickr && input.name) {
      // Use the formatted value from flatpickr
      data[input.name] = input.value;
    }
  });
  
  return JSON.stringify(data);
}

/**
 * Check if form has been modified from original state
 * @param {HTMLFormElement} form - The form element
 * @returns {boolean} True if form has unsaved changes
 */
function hasFormChanged(form) {
  const originalData = formOriginalData.get(form);
  if (!originalData) return false;
  
  const currentData = getFormData(form);
  return originalData !== currentData;
}

/**
 * Update the global unsaved changes flag based on all monitored forms
 */
function updateGlobalChangeStatus() {
  hasUnsavedChanges = false;
  
  monitoredForms.forEach(form => {
    if (document.body.contains(form) && hasFormChanged(form)) {
      hasUnsavedChanges = true;
    }
  });
}

/**
 * Handle form input changes
 * @param {Event} event - The input event
 */
function handleFormChange(event) {
  const form = event.target.closest('form');
  if (form && monitoredForms.has(form)) {
    updateGlobalChangeStatus();
  }
}

/**
 * Mark form as saved (reset to current state)
 * @param {HTMLFormElement} form - The form element
 */
function markFormAsSaved(form) {
  if (!form) return;
  
  // Update the original data to current state
  formOriginalData.set(form, getFormData(form));
  updateGlobalChangeStatus();
}

/**
 * Enable unsaved changes detection for a specific form
 * @param {HTMLFormElement|string} formOrSelector - Form element or selector
 */
function enableUnsavedChangesDetection(formOrSelector) {
  const form = typeof formOrSelector === 'string' 
    ? document.querySelector(formOrSelector) 
    : formOrSelector;
  
  if (!form || !(form instanceof HTMLFormElement)) {
    return;
  }
  
  // Skip if already monitoring this form
  if (monitoredForms.has(form)) return;
  
  // Add to monitored forms first
  monitoredForms.add(form);
  
  // Store original form data after a delay to allow CKEditor/Flatpickr to initialize
  setTimeout(() => {
    formOriginalData.set(form, getFormData(form));
  }, 1000);
  
  // Listen to all input changes (using event delegation for dynamic content)
  form.addEventListener('input', handleFormChange, true); // Use capture for dynamic elements
  form.addEventListener('change', handleFormChange, true); // Use capture for dynamic elements
  
  // Mark as saved when form is submitted successfully
  form.addEventListener('submit', function() {
    // Immediately clear the global flag to prevent alerts during navigation
    hasUnsavedChanges = false;
    markFormAsSaved(form);
  });
  
  // Function to attach CKEditor listeners
  const attachCKEditorListeners = () => {
    if (typeof CKEDITOR === 'undefined') return;
    
    const textareas = form.querySelectorAll('textarea');
    textareas.forEach(textarea => {
      if (textarea.id && CKEDITOR.instances[textarea.id]) {
        const editor = CKEDITOR.instances[textarea.id];
        
        // Check if we haven't attached listeners yet
        if (!editor.___changeListenerAttached) {
          // Listen to CKEditor change events
          editor.on('change', function() {
            handleFormChange({ target: textarea });
          });
          
          // Also listen to key events for immediate detection
          editor.on('key', function() {
            handleFormChange({ target: textarea });
          });
          
          editor.___changeListenerAttached = true;
        }
      }
    });
  };
  
  // Try to attach CKEditor listeners multiple times to catch late initialization
  if (typeof CKEDITOR !== 'undefined') {
    setTimeout(attachCKEditorListeners, 500);
    setTimeout(attachCKEditorListeners, 1000);
    setTimeout(attachCKEditorListeners, 2000);
  }
  
  // Listen to Flatpickr datetime/date/time picker changes
  setTimeout(() => {
    // Find all inputs that might have flatpickr
    const dateTimeInputs = form.querySelectorAll('input[type="text"]');
    dateTimeInputs.forEach(input => {
      // Check if this input has flatpickr instance
      if (input._flatpickr) {
        const flatpickrInstance = input._flatpickr;
        
        // Add onChange callback to detect changes
        const originalOnChange = flatpickrInstance.config.onChange || [];
        flatpickrInstance.config.onChange = [...originalOnChange, function() {
          handleFormChange({ target: input });
        }];
      }
    });
  }, 500);
  
  const observer = new MutationObserver(function(mutations) {
    let hasStructuralChange = false;
    
    // Check if any nodes were added or removed
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        // Check for removed nodes
        if (mutation.removedNodes.length > 0) {
          mutation.removedNodes.forEach(node => {
            // Check if removed node is significant (has inputs, questions, reminders, etc.)
            if (node.nodeType === 1) { // Element node
              const hasInputs = node.querySelector && (
                node.querySelector('input') || 
                node.querySelector('textarea') || 
                node.querySelector('select') ||
                node.classList?.contains('question-item') ||
                node.classList?.contains('choice-input-description') ||
                node.classList?.contains('reminder-block') ||
                node.classList?.contains('reminder-time-item')
              );
              if (hasInputs || node.matches?.('input, textarea, select')) {
                hasStructuralChange = true;
              }
            }
          });
        }
        
        // Check for added nodes
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              const hasInputs = node.querySelector && (
                node.querySelector('input') || 
                node.querySelector('textarea') || 
                node.querySelector('select') ||
                node.classList?.contains('question-item') ||
                node.classList?.contains('choice-input-description') ||
                node.classList?.contains('reminder-block') ||
                node.classList?.contains('reminder-time-item')
              );
              if (hasInputs || node.matches?.('input, textarea, select')) {
                hasStructuralChange = true;
              }
            }
          });
        }
      }
    });
    
    // If structural changes detected, trigger change detection
    if (hasStructuralChange) {
      handleFormChange({ target: form });
    }
    
    // Check for newly added CKEditor instances
    if (typeof CKEDITOR !== 'undefined') {
      const textareas = form.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        if (textarea.id && CKEDITOR.instances[textarea.id]) {
          const editor = CKEDITOR.instances[textarea.id];
          
          // Check if we haven't attached listeners yet
          if (!editor.___changeListenerAttached) {
            editor.on('change', function() {
              handleFormChange({ target: textarea });
            });
            editor.on('key', function() {
              handleFormChange({ target: textarea });
            });
            editor.___changeListenerAttached = true;
          }
        }
      });
    }
    
    // Check for newly added flatpickr instances
    const dateTimeInputs = form.querySelectorAll('input[type="text"]');
    dateTimeInputs.forEach(input => {
      if (input._flatpickr && !input.___flatpickrListenerAttached) {
        const flatpickrInstance = input._flatpickr;
        const originalOnChange = flatpickrInstance.config.onChange || [];
        flatpickrInstance.config.onChange = [...originalOnChange, function() {
          handleFormChange({ target: input });
        }];
        input.___flatpickrListenerAttached = true;
      }
    });
  });
  
  // Start observing the form for changes
  observer.observe(form, {
    childList: true,
    subtree: true,
    attributes: false // Don't watch attributes to reduce noise
  });
}

/**
 * Disable unsaved changes detection for a specific form
 * @param {HTMLFormElement|string} formOrSelector - Form element or selector
 */
function disableUnsavedChangesDetection(formOrSelector) {
  const form = typeof formOrSelector === 'string' 
    ? document.querySelector(formOrSelector) 
    : formOrSelector;
  
  if (!form || !(form instanceof HTMLFormElement)) return;
  
  // Remove event listeners
  form.removeEventListener('input', handleFormChange);
  form.removeEventListener('change', handleFormChange);
  
  // Remove from tracked forms
  monitoredForms.delete(form);
  formOriginalData.delete(form);
  
  updateGlobalChangeStatus();
}



/**
 * Show browser's native reload confirmation ONLY for reload, not back/forward
 * @param {Event} event - The beforeunload event
 * @returns {string|undefined} Confirmation message
 */
function handleBeforeUnload(event) {
  if (hasUnsavedChanges && !leaveInProgress) {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }
}

/**
 * Handle clicks on navigation links (including admin sidebar)
 * @param {Event} event - The click event
 */
function handleLinkClick(event) {
  // Only check if there are unsaved changes
  if (!hasUnsavedChanges) return;
  const link = event.target.closest('a');
  if (!link) return;
  // Skip if it's the same page (hash links)
  // Ignore CKEditor toolbar/dialog links and non-navigation hrefs
  const href = (link.getAttribute('href') || '').trim();
  if (!href || href.startsWith('#') || href.toLowerCase().startsWith('javascript:')) return;
  if (link.closest('.cke')) return;

  if (link.hasAttribute('data-method')) return;
  if (link.target === '_blank') return;

  // Check if it's navigating away from current page
  let targetPath;
  try {
    targetPath = new URL(href, window.location.origin).pathname;
  } catch (e) {
    return;
  }
  const currentPath = window.location.pathname;
  if (currentPath !== targetPath) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    window.location.href = href;
  }
}

/**
 * Initialize unsaved changes detection for all forms with data-unsaved-changes attribute
 * or forms within elements with data-unsaved-changes attribute
 */
function initUnsavedChangesDetection() {
  // Auto-enable for forms with data-unsaved-changes="true" attribute
  const autoEnableForms = document.querySelectorAll('form[data-unsaved-changes="true"]');
  autoEnableForms.forEach(form => {
    enableUnsavedChangesDetection(form);
  });
  
  // Also check for elements with data-unsaved-changes that contain forms
  const autoEnableContainers = document.querySelectorAll('[data-unsaved-changes="true"]');
  autoEnableContainers.forEach(container => {
    const forms = container.querySelectorAll('form');
    forms.forEach(form => {
      enableUnsavedChangesDetection(form);
    });
  });
}

/**
 * Clean up forms that are no longer in the DOM
 */
function cleanupRemovedForms() {
  monitoredForms.forEach(form => {
    if (!document.body.contains(form)) {
      disableUnsavedChangesDetection(form);
    }
  });
}

// Setup global event listeners
window.addEventListener('popstate', function() {
  if (suppressNextPopstate) {
    suppressNextPopstate = false;
    return;
  }
  if (!hasUnsavedChanges) return;
  const confirmed = confirm('Leave site?\nChanges you made may not be saved.');
  if (!confirmed) {
    suppressNextPopstate = true;
    history.forward();
    return;
  }
  leaveInProgress = true;
  hasUnsavedChanges = false;
});

// Show browser's native reload confirmation (but NOT for back/forward)
window.addEventListener('beforeunload', handleBeforeUnload);

(function(){
  const originalSubmit = HTMLFormElement.prototype.submit;
  if (originalSubmit && !HTMLFormElement.prototype.___patchedSubmit) {
    HTMLFormElement.prototype.submit = function() {
      leaveInProgress = true;
      hasUnsavedChanges = false;
      try { markFormAsSaved(this); } catch (_) {}
      return originalSubmit.call(this);
    };
    HTMLFormElement.prototype.___patchedSubmit = true;
  }
})();

// Intercept navigation clicks (including admin sidebar)
document.addEventListener('click', handleLinkClick, true); // Use capture phase




// Clean up on Turbolinks navigation
document.addEventListener('turbolinks:before-cache', function() {
  // Clean up forms before caching
  cleanupRemovedForms();
});

// Reset state when navigating away
document.addEventListener('turbolinks:load', function() {
  // Clean up any stale forms
  cleanupRemovedForms();
});

/**
 * Debug helper to check if form has unsaved changes
 * Usage in console: window.checkUnsavedChanges()
 */
function checkUnsavedChanges() {
  console.log('=== Unsaved Changes Debug ===');
  console.log('Has unsaved changes:', hasUnsavedChanges);
  console.log('Number of monitored forms:', monitoredForms.size);
  
  monitoredForms.forEach((form, index) => {
    console.log(`\nForm ${index + 1}:`, form.id || form);
    console.log('Form has changed:', hasFormChanged(form));
    console.log('Original data length:', formOriginalData.get(form)?.length || 0);
    console.log('Current data length:', getFormData(form)?.length || 0);
  });
  
  console.log('=== End Debug ===');
}

// Expose public API
export {
  initUnsavedChangesDetection,
  enableUnsavedChangesDetection,
  disableUnsavedChangesDetection,
  markFormAsSaved,
  checkUnsavedChanges
};

// Also expose via window for easy access in inline scripts
window.markFormAsSaved = markFormAsSaved;
window.disableUnsavedChangesDetection = disableUnsavedChangesDetection;
window.checkUnsavedChanges = checkUnsavedChanges;

